// Takes the sound out of a video in the browser: 16 kHz mono, which is what the speech recogniser wants (and small enough to upload quickly).
import { activityOf, encodeWav } from "./activity";

export const MAX_MINUTES = 20;                 // the decoded sound of a long video would not fit in the memory of a browser tab
export const MAX_VIDEO_BYTES = 600 * 1024 * 1024;
const RATE = 16000;

// A recording that is quiet is made louder (a recogniser hears quiet speech badly); one that is loud enough is left alone.
export function normalise(samples) {
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) { const a = Math.abs(samples[i]); if (a > peak) peak = a; }
  if (peak > 0 && peak < 0.5) {
    const gain = Math.min(20, 0.8 / peak);
    for (let i = 0; i < samples.length; i += 1) samples[i] *= gain;
  }
  return samples;
}

// Resolves with { wav (Blob), activity, seconds }. Rejects with a message for the visitor when the video has no sound the browser can read.
export async function extractAudio(file, onStep = () => {}) {
  onStep("Reading the video...");
  const context = new (window.AudioContext || window.webkitAudioContext)();
  let decoded;
  try {
    const bytes = await file.arrayBuffer();
    onStep("Opening its sound...");
    decoded = await context.decodeAudioData(bytes);
  } catch {
    throw new Error("The sound of this video could not be read by the browser. It may have no sound, or use a sound format this browser cannot play. Try an MP4 with AAC sound, or a WebM.");
  } finally {
    context.close().catch(() => {});
  }
  if (!decoded.length || decoded.duration < 1) throw new Error("This video has no sound to write subtitles for.");
  if (decoded.duration > MAX_MINUTES * 60) throw new Error(`This video is ${Math.round(decoded.duration / 60)} minutes long. Subtitles can be made for up to ${MAX_MINUTES} minutes at a time - cut it into shorter parts.`);
  onStep("Preparing the sound...");
  const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * RATE), RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  const samples = normalise(new Float32Array(rendered.getChannelData(0)));
  return { wav: new Blob([encodeWav(samples, RATE)], { type: "audio/wav" }), activity: activityOf(samples, RATE), seconds: decoded.duration };
}
